import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import { useChannelIcon } from '@/core/hooks/useChannelIcon';
import { normalizeMetricsChannel } from '../../metricsMenuPaths';

import './MetricsPageSelector.scss';

export interface PageOption {
  id: string;
  title: string;
  username: string;
  profile_pic: string;
  channel: string;
  acc_id: number;
  externalUrl: string;
}

export const MOCK_PAGES: PageOption[] = [
  {
    id: 'mock-facebook-batima',
    title: 'Bátima e Robisão',
    username: 'batimaerobisao',
    profile_pic: '',
    channel: 'facebook',
    acc_id: 1,
    externalUrl: 'https://facebook.com/batimaerobisao',
  },
  {
    id: 'mock-facebook-capivara',
    title: 'Capivara Esperta',
    username: 'capivaraesperta',
    profile_pic: '',
    channel: 'facebook',
    acc_id: 2,
    externalUrl: 'https://facebook.com/capivaraesperta',
  },
   {
    id: '1',
    title: 'CE Instagram',
    username: 'CE Instagram',
    profile_pic: '',
    channel: 'instagram',
    acc_id: 2,
    externalUrl: 'https://instagram.com/CE Instagram',
  },
  {
    id: '2',
    title: 'TikTok',
    username: 'TikTok',
    profile_pic: '',
    channel: 'TikTok',
    acc_id: 2,
    externalUrl: 'https://tiktok.com/TikTok',
  },
  {
    id: '3',
    title: 'twitter',
    username: 'twitter',
    profile_pic: '',
    channel: 'twitter',
    acc_id: 2,
    externalUrl: 'https://twitter.com/twitter',
  },
  {
    id: '4',
    title: 'youtube',
    username: 'youtube',
    profile_pic: '',
    channel: 'youtube',
    acc_id: 2,
    externalUrl: 'https://youtube.com/youtube',
  },
  {
    id: '5',
    title: 'linkedin',
    username: 'linkedin',
    profile_pic: '',
    channel: 'linkedin',
    acc_id: 5,
    externalUrl: 'https://linkedin.com/linkedin',
  },
  {
    id: '6',
    title: 'threads',
    username: 'threads',
    profile_pic: '',
    channel: 'threads',
    acc_id: 2,
    externalUrl: 'https://threads.com/threads',
  },
  {
    id: '7',
    title: 'googleplay',
    username: 'googleplay',
    profile_pic: '',
    channel: 'googleplay',
    acc_id: 2,
    externalUrl: 'https://play.google.com/store/apps/details?id=com.googleplay',
  },
  {
    id: 'mock-googlemybusiness-loja',
    title: 'Google Meu Negócio',
    username: 'Av. Reg. Feijó, 173, São Paulo - SP 03342-900',
    profile_pic: '',
    channel: 'googlemybusiness',
    acc_id: 2,
    externalUrl: 'https://www.google.com/maps/search/?api=1&query=Loja+Exemplo',
  },
  {
    id: '62424234423',
    title: 'Capivara Espertaaaaaaaaaaaaaaaaaaaaaa',
    username: 'bastanteeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
    profile_pic: '',
    channel: 'threads',
    acc_id: 2,
    externalUrl: 'https://threads.com/bastanteeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
  }
];

const DEFAULT_PAGES: PageOption[] = process.env.NODE_ENV === 'development' ? MOCK_PAGES : [];

export interface MetricsPageSelectorProps {
  options?: PageOption[];
  selectedPageId?: string;
  onChange: (selected: PageOption) => void;
  onSearch?: (searchTerm: string) => void;
}

const negativeIconChannels = new Set(['tiktok', 'twitter', 'threads']);

const getIconType = (channel: string) => 
  (negativeIconChannels.has(channel) && 'negative') ||
  'default';

const PageChannelIcon: React.FC<{ channel: string }> = ({ channel }) => {
  const icons = useChannelIcon(channel)?.icons;
  const iconType = getIconType(channel);
  const iconUrl = icons?.[iconType] || icons?.default;

  return (
    <span className="metrics-page-selector__channel-icon" data-channel={channel} aria-hidden="true">
      {iconUrl ? (
        <img src={iconUrl} alt="" width={24} height={24} />
      ) : (
        <BdsIcon theme="outline" type="logo" name={channel} size="medium" />
      )}
    </span>
  );
};

const PageAvatar: React.FC<{ page: PageOption }> = ({ page }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const initials = page.title.trim().split(/\s+/).slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();

  return (
    <span className="metrics-page-selector__avatar" aria-hidden="true">
      {page.profile_pic && !imageFailed ? (
        <img src={page.profile_pic} alt="" onError={() => setImageFailed(true)} />
      ) : initials}
    </span>
  );
};

export const MetricsPageSelector: React.FC<MetricsPageSelectorProps> = ({
  options = DEFAULT_PAGES,
  selectedPageId,
  onChange,
  onSearch,
}) => {
  const normalizedOptions = options.map((page) => ({ ...page, channel: normalizeMetricsChannel(page.channel) }));
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selection, setSelection] = useState({ id: selectedPageId, sourceId: selectedPageId });
  const rootRef = useRef<HTMLDivElement>(null);
  const activatorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const submittedSearchRef = useRef<string | null>(null);
  const dropdownId = useId();
  const currentId = selection.sourceId === selectedPageId ? selection.id : selectedPageId;
  const selectedPage = normalizedOptions.find((page) => page.id === currentId) ?? normalizedOptions[0];
  const query = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = normalizedOptions.filter((page) =>
    `${page.title} ${page.username}`.toLocaleLowerCase().includes(query),
  );

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();

    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      activatorRef.current?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const submitSearch = () => {
    if (submittedSearchRef.current === searchTerm) return;
    submittedSearchRef.current = searchTerm;
    onSearch?.(searchTerm);
  };

  const selectPage = (page: PageOption) => {
    setSelection({ id: page.id, sourceId: selectedPage?.id });
    setIsOpen(false);
    onChange(page);
    activatorRef.current?.focus();
  };

  return (
    <div className="metrics-page-selector" ref={rootRef}>
      <button
        ref={activatorRef}
        type="button"
        className="metrics-page-selector__activator"
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        disabled={normalizedOptions.length === 0}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        {selectedPage && <PageChannelIcon channel={selectedPage.channel} />}
        <span className="metrics-page-selector__title">{selectedPage?.title ?? t('metricsMenu.selectPage')}</span>
        <BdsIcon name="arrow-down" theme="outline" class="metrics-page-selector__chevron" aria-hidden="true" />
      </button>
        <div
          id={dropdownId}
          className="metrics-page-selector__dropdown"
          data-expanded={isOpen}
          aria-hidden={!isOpen}
          inert={!isOpen}
        >
          <div className="metrics-page-selector__search">
            <BdsIcon name="search" theme="outline" class="metrics-page-selector__search-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              placeholder={t('metricsMenu.searchPages')}
              aria-label={t('metricsMenu.searchPages')}
              onChange={(event) => setSearchTerm(event.target.value)}
              onBlur={submitSearch}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitSearch();
                }
              }}
            />
          </div>
          <ul className="metrics-page-selector__options">
            {filteredOptions.map((page) => (
              <li key={page.id}>
                <button
                  type="button"
                  className="metrics-page-selector__option"
                  data-page-id={page.id}
                  aria-pressed={page.id === selectedPage?.id}
                  onClick={() => selectPage(page)}
                >
                  <PageChannelIcon channel={page.channel} />
                  <PageAvatar key={`${page.id}:${page.profile_pic}`} page={page} />
                  <span className="metrics-page-selector__title">{page.title}</span>
                </button>
              </li>
            ))}
          </ul>
          {filteredOptions.length === 0 && <output className="metrics-page-selector__empty">{t('metricsMenu.noPages')}</output>}
        </div>
    </div>
  );
};
